import { useMemo } from 'react';
import { Tooltip } from '../../../../components/popover/tooltip';
import { defineColumn, type StrictColumnDef } from '../../../../components/tanstack-table/column';
import type { OrderOptions } from '../../../../data/order-options';
import { minQOHThreshold } from '../../../../data/product';
import { itemHasUserCount, type ProductOrderItem } from '../data/product-order-item';

export type ReorderLabel = 'USER' | 'DISABLED' | 'AUTO_DSBL' | 'YES' | 'MIN_QOH' | 'ROLLOFF' | 'NO';

/**
 * The server's `suggestion_reason` values (pkg/ordermath: THRESHOLD,
 * BELOW_PACK, MIN_QOH, ROLLOFF). Finer than ReorderLabel because the two sales
 * predicates share the YES label but not its tooltip copy.
 *
 * Deliberately not a closed union on the wire — see OrderItemSchema. Anything
 * this client doesn't recognise falls through to 'NO' rather than dropping the
 * row.
 */
const labelForReason: Record<string, ReorderLabel> = {
  THRESHOLD: 'YES',
  BELOW_PACK: 'YES',
  MIN_QOH: 'MIN_QOH',
  ROLLOFF: 'ROLLOFF',
};

/** The two reorder settings the label and its explanation depend on. */
type ReorderSettings = Pick<OrderOptions, 'qoh_threshold_weeks' | 'reorder_weeks'>;

/**
 * Suggestion state for a row, ignoring any count the store owner typed. Sorting
 * reads this directly so a row keeps its place in the reorder ranking once
 * counted, rather than jumping to the USER bucket.
 */
export function reorderLabel(
  orderItem: ProductOrderItem,
  { reorder_weeks }: ReorderSettings
): ReorderLabel {
  if (orderItem.product.disabled) {
    return 'DISABLED';
  }

  if (orderItem.product.auto_disabled) {
    return 'AUTO_DSBL';
  }

  if (reorder_weeks === 0) {
    return 'NO';
  }

  // Everything below used to re-derive the four suggestion predicates here,
  // off the product row, which meant it could disagree with the quantity the
  // server actually suggested. It now just reports what the server decided.
  //
  // No reason means no suggestion, so 'NO' is right. That holds because
  // GetSuggestion returns nonzero whenever any predicate fires, and
  // expandAndPrune only emits an item for a nonzero suggestion or a
  // user-touched row — so a row with no reason either has no order item at all
  // (buildRows maps the catalog, not the order) or was written before this
  // field shipped, and in both cases nothing is being suggested.
  return labelForReason[orderItem.suggestion_reason ?? ''] ?? 'NO';
}

/** What the cell actually shows — a typed count wins over the suggestion. */
export function displayLabel(orderItem: ProductOrderItem, settings: ReorderSettings): ReorderLabel {
  return itemHasUserCount(orderItem) ? 'USER' : reorderLabel(orderItem, settings);
}

/**
 * Plain-language reason behind a label. Every label gets one — the column gloss
 * promises as much, and a bare cell leaves the store owner guessing why a
 * product did or didn't make the order. The switch is exhaustive over
 * ReorderLabel so a new label can't ship without copy.
 *
 * Copy carries the *why* only. The label already says whether Orderbot is
 * suggesting a reorder, and the Suggested column says how much — repeating
 * either just buries the one thing the cell can't show.
 */
export function reorderTooltip(
  orderItem: ProductOrderItem,
  label: ReorderLabel,
  { qoh_threshold_weeks, reorder_weeks }: ReorderSettings
): string {
  switch (label) {
    case 'USER':
      return 'You entered your own quantity for this item.';

    case 'DISABLED':
      return 'You disabled this product.';

    case 'AUTO_DSBL':
      return "This hasn't been selling. Suggestions resume automatically once sales pick up.";

    case 'YES':
      // The label collapses the two sales predicates; the copy doesn't. This
      // is why the wire value is finer-grained than ReorderLabel.
      return orderItem.suggestion_reason === 'THRESHOLD'
        ? `Stock is below your QOH threshold of ${qoh_threshold_weeks} week(s) of sales.`
        : 'Stock is down to less than a full pack.';

    case 'MIN_QOH':
      return `Stock is below your Min QOH of ${minQOHThreshold(orderItem.product)}.`;

    case 'ROLLOFF':
      return "You're out of stock but this has sold recently, so it doesn't roll off the shelf.";

    case 'NO':
      return reorder_weeks === 0
        ? 'Reorder Weeks is set to 0 in your Reorder Settings.'
        : `Stock is above your QOH threshold of ${qoh_threshold_weeks} week(s) of sales.`;
  }
}

export function useReorder(options: OrderOptions): StrictColumnDef<ProductOrderItem> {
  // Depend on primitives, not the options object reference, so reorderColumn
  // stays stable across refreshes that ship a new options object with the same
  // values — avoids cascading column-def recomputation and the TanStack cell
  // remounts that follow. Hygiene rather than a bug fix today: cells are
  // stateless, so a remount has no visible effect.
  const { qoh_threshold_weeks, reorder_weeks } = options;

  return useMemo(() => {
    const settings = { qoh_threshold_weeks, reorder_weeks };

    const labelToSortOrder: Record<string, number> = {
      DISABLED: -1,
      AUTO_DSBL: -1,
      YES: 2,
      MIN_QOH: 2,
      ROLLOFF: 1,
      NO: 0,
    };

    return defineColumn<ProductOrderItem>({
      id: 'Reorder',
      minWidth: 70,
      accessorFn: (orderItem) => displayLabel(orderItem, settings),
      cell: ({ item: orderItem }) => {
        const label = displayLabel(orderItem, settings);

        return (
          <Tooltip content={reorderTooltip(orderItem, label, settings)}>
            <div className={`text-center has-tooltip ${label === 'USER' ? 'font-extrabold' : ''}`}>
              {label}
            </div>
          </Tooltip>
        );
      },
      sortKeyFn: (orderItem) => labelToSortOrder[reorderLabel(orderItem, settings)],
      gloss: 'Whether Orderbot is suggesting this for reorder. Hover the cell to see why.',
    });
  }, [qoh_threshold_weeks, reorder_weeks]);
}
